import debounce from 'lodash/debounce';
import React, { useEffect, useRef, useState } from 'react';
import useOnClickOutside from 'shared/hooks/interactions/useOnClickOutside';
import useScrollAreaIntersection from 'shared/hooks/interactions/useScrollAreaIntersection';
import useScrollObserver from 'shared/hooks/interactions/useScrollObserver';
import styled from 'styled-components';
import { Theme } from 'theme';

import { OverflowMenuIcon } from '../../img';
import { Centered } from '../centered';
import Portal from '../Portal';
import MenuItem, { OverflowMenuItemProps } from './MenuItem';
import  OverflowMenuPanel from './OverflowMenuPanel';

export interface OverflowMenuProps {
    big?: boolean;
    className?: string;
    isWhite?: boolean;
    items: Array<OverflowMenuItemProps>;
    fixPosition?: boolean;
    viewportMargin?: string;
}

interface Coords {
    right: number;
    top: number;
}

const OverflowMenu: React.FC<OverflowMenuProps> = ({
    big = false,
    className,
    isWhite = false,
    items,
    fixPosition = false,
    viewportMargin
}) => {
    const [ open, setOpen ] = useState<boolean>(false);
    const [ coords, setCoords ] = useState<Coords | null>(null);

    const ref = useRef<HTMLDivElement>(null);

    const scrolled = (isIntersecting: boolean) => {
        if (!isIntersecting) {
            setOpen(false);
        }
    };

    useScrollAreaIntersection( ref.current, scrolled, open, viewportMargin);

    useOnClickOutside(ref.current, () => setOpen(false));

    const updatePopupCoords = () => {
        if (!ref || !ref.current) {
            return;
        }

        const rect = ref.current.getBoundingClientRect();

        const width  = document.body.clientWidth;

        const coords: Coords = {
            right: width - (rect.x + rect.width),
            top: rect.y + rect.height + (fixPosition ? 0: window.scrollY)
        };

        setCoords(coords);
    };

    useScrollObserver(ref.current, updatePopupCoords);

    if (open && !coords) {
        updatePopupCoords();
    }

    useEffect(() => {
        const updateCoords = debounce(updatePopupCoords, 100);

        window.addEventListener('resize', updateCoords);

        return () => window.removeEventListener('resize', updateCoords);
    }, []);

    return (
        <Root className={ className }
            onClick={ () => setOpen(!open) }
            ref={ ref }
        >
            <Button big={ big }
                isWhite={ isWhite }
                open={ open }
            >
                <StyledOverflowMenuIcon big={ big } />
            </Button>

            { open && coords &&
            <Portal>
                <StyledOverflowMenuPanel { ...coords }
                    className="skip-click"
                    withButton
                    isWhite={ isWhite }
                    wideButton={ big }
                    fixPosition={ fixPosition }
                >
                    { items.map((item, index) => (
                        <MenuItem key={ index }
                            item={ item }
                            hideShadow={ index === 0 }
                        />
                    )) }
                </StyledOverflowMenuPanel>
            </Portal>
            }
        </Root>
    );
};

export default OverflowMenu;

const getButtonBG = ({ isWhite, open, theme }: {
    isWhite: boolean;
    open: boolean;
    theme: Theme;
}) => {
    if (!open) {
        return 'transparent';
    }

    if (isWhite) {
        return 'white';
    }

    return theme.semanticColor.ui_01;
};

const Button = styled(Centered)<{ big: boolean; isWhite: boolean; open: boolean }>`
    background-color: ${ getButtonBG };

    ${ ({ big, theme, open }) => `
        border-radius: ${ open ? `${ theme.radius.radius_5 } ${ theme.radius.radius_5 } 0 0`  : '50%' };
        box-shadow: ${ open ? theme.shadow.shadow_2 : 'none' };
        height: ${ big ? theme.size.size_50 : theme.size.size_40 };
        width: ${ big ? theme.size.size_50 : theme.size.size_40 };
    ` };

    @media (hover: hover) {
        &:hover {
            ${ ({ theme, open }) => !open && `
                background-color: ${ theme.color.turquoise.turquoise_20 };
                border-radius: 50%;
            ` };
        }
    }

    :active {
        ${ ({ theme, open }) => !open && `
            background-color: ${ theme.color.turquoise.turquoise_30 };
            border-radius: 50%;
        ` };
    }
`;

const StyledOverflowMenuIcon = styled(OverflowMenuIcon)<{ big: boolean }>`
  ${ ({ big, theme }) => `
        height: ${ big ? theme.size.size_25 : theme.size.size_20 };
        width: ${ big ? theme.size.size_25 : theme.size.size_20 };
    ` };
  
`;

const Root = styled.div`
  cursor: pointer;
  position: relative;
`;

const StyledOverflowMenuPanel = styled(OverflowMenuPanel)<Coords & { fixPosition: boolean }>`
  position: ${ ({ fixPosition }) => (fixPosition ? 'fixed' : 'absolute') };

  ${ ({ right, top }) => `
        right: ${ right }px;
        top: ${ top }px;
    ` }
`;
